import React, { useState, useEffect } from 'react';
import { base44 } from '@/api/base44Client';
import { useQuery } from '@tanstack/react-query';
import { Swords, Timer, Trophy, X } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { calculateDuelTag, isSeen, markSeen } from './lib/notificationDedupe';

export default function FloatingNotifications({ user, onPlaygroundSelect }) {
    const [dismissedNotifications, setDismissedNotifications] = useState(new Set());
    const navigate = useNavigate();

    const { data: activeDuels = [] } = useQuery({
        queryKey: ['userActiveDuels', user?.id],
        queryFn: async () => {
            if (!user?.id) return [];
            
            // Fetch duels where user is attacker or defender
            const asAttacker = await base44.entities.CourtDuel.filter({
                attacker_user_id: user.id,
                status: 'ACTIVE'
            });
            
            const asDefender = await base44.entities.CourtDuel.filter({
                defender_user_id: user.id,
                status: 'ACTIVE'
            });

            return [...asAttacker, ...asDefender];
        },
        enabled: !!user?.id,
        refetchInterval: 60000, // Refetch every 15 seconds
    });

    const { data: pendingDuels = [] } = useQuery({
        queryKey: ['userPendingDuels', user?.id],
        queryFn: async () => {
            if (!user?.id) return [];
            
            // Fetch duels where user is defender and status is PENDING_ACCEPT
            const pending = await base44.entities.CourtDuel.filter({
                defender_user_id: user.id,
                status: 'PENDING_ACCEPT'
            });

            return pending;
        },
        enabled: !!user?.id,
        refetchInterval: 60000, // Refetch every 15 seconds
    });

    const { data: recentVictories = [] } = useQuery({
        queryKey: ['userRecentVictories', user?.id],
        queryFn: async () => {
            if (!user?.id) return [];
            
            const fiveMinutesAgo = new Date(Date.now() - 5 * 60 * 1000).toISOString();
            
            // Fetch completed duels where user won in the last 5 minutes
            const victories = await base44.entities.CourtDuel.filter({
                winner_user_id: user.id,
                status: 'COMPLETED'
            }, '-created_at', 10);

            return victories.filter(v => new Date(v.created_at) >= new Date(fiveMinutesAgo));
        },
        enabled: !!user?.id,
        refetchInterval: 60000,
    });

    // Fetch playground details for all duels
    const { data: playgroundsMap = {} } = useQuery({
        queryKey: ['duelPlaygrounds', [...activeDuels, ...pendingDuels, ...recentVictories].map(d => d.playground_id)],
        queryFn: async () => {
            const allDuels = [...activeDuels, ...pendingDuels, ...recentVictories];
            if (allDuels.length === 0) return {};
            
            const playgroundIds = [...new Set(allDuels.map(d => d.playground_id))];
            const playgrounds = await Promise.all(
                playgroundIds.map(id => base44.entities.Playground.filter({ id }).then(r => r[0]))
            );
            
            const map = {};
            playgrounds.forEach(p => {
                if (p) map[p.id] = p;
            });
            return map;
        },
        enabled: [...activeDuels, ...pendingDuels, ...recentVictories].length > 0,
    });

    const handleNotificationClick = (playgroundId, playground) => {
        // Always try the global callback first (set by Home.jsx)
        if (playground && window.__onPlaygroundSelect) {
            window.__onPlaygroundSelect(playground);
        } else if (onPlaygroundSelect && playground) {
            onPlaygroundSelect(playground);
        } else if (playgroundId) {
            navigate(`/?selected=${playgroundId}`);
        }
    };

    const handleDismiss = (notifId, eventTag, e) => {
        e.stopPropagation();
        setDismissedNotifications(prev => new Set([...prev, notifId]));
        // Marquer comme vu pour éviter réapparition
        if (eventTag) {
            markSeen(eventTag);
        }
    };

    // Create notification items with deduplication
    const notifications = [
        ...recentVictories.map(duel => {
            const eventTag = calculateDuelTag(duel, user.id);
            return {
                id: `victory-${duel.id}`,
                eventTag,
                type: 'victory',
                duel,
                playground: playgroundsMap[duel.playground_id],
                message: 'Victoire ! 🎉',
                icon: Trophy,
                color: 'bg-green-500'
            };
        }),
        ...pendingDuels.map(duel => {
            const eventTag = calculateDuelTag(duel, user.id);
            return {
                id: `pending-${duel.id}`,
                eventTag,
                type: 'attack',
                duel,
                playground: playgroundsMap[duel.playground_id],
                message: 'Playground sous attaque !',
                icon: Swords,
                color: 'bg-red-500'
            };
        }),
        ...activeDuels.map(duel => {
            const eventTag = calculateDuelTag(duel, user.id);
            return {
                id: `active-${duel.id}`,
                eventTag,
                type: 'match',
                duel,
                playground: playgroundsMap[duel.playground_id],
                message: 'Match en cours',
                icon: Timer,
                color: 'bg-orange-500'
            };
        })
    ].filter(notif => {
        // Filter dismissed
        if (dismissedNotifications.has(notif.id)) return false;
        // Filter already seen via push (déduplication)
        if (notif.eventTag && isSeen(notif.eventTag)) return false;
        return true;
    });

    return (
        <div className="fixed top-20 right-4 z-10 space-y-2 max-w-sm w-80 pointer-events-none">
            <AnimatePresence>
                {notifications.map((notif) => {
                    const Icon = notif.icon;
                    return (
                        <motion.div
                            key={notif.id}
                            initial={{ opacity: 0, x: 100, scale: 0.8 }}
                            animate={{ opacity: 1, x: 0, scale: 1 }}
                            exit={{ opacity: 0, x: 100, scale: 0.8 }}
                            transition={{ duration: 0.3 }}
                            className={`${notif.color} text-white p-4 rounded-lg shadow-2xl cursor-pointer hover:scale-105 transition-transform pointer-events-auto`}
                            onClick={() => handleNotificationClick(notif.playground?.id, notif.playground)}
                        >
                            <div className="flex items-start gap-3">
                                <Icon className="w-6 h-6 mt-0.5 flex-shrink-0" />
                                <div className="flex-1 min-w-0">
                                    <p className="font-bold text-sm">{notif.message}</p>
                                    <p className="text-xs opacity-90 mt-1 truncate">
                                        {notif.playground?.name || 'Chargement...'}
                                    </p>
                                    {notif.type === 'attack' && (
                                        <p className="text-xs opacity-75 mt-1">
                                            Attaqué par {notif.duel.attacker_name}
                                        </p>
                                    )}
                                    {notif.type === 'victory' && (
                                        <p className="text-xs opacity-75 mt-1">
                                            Vous avez gagné le duel !
                                        </p>
                                    )}
                                </div>
                                <button
                                    onClick={(e) => handleDismiss(notif.id, notif.eventTag, e)}
                                    className="text-white/80 hover:text-white transition-colors flex-shrink-0"
                                >
                                    <X className="w-4 h-4" />
                                </button>
                            </div>
                        </motion.div>
                    );
                })}
            </AnimatePresence>
        </div>
    );
}